'use client';
import React from 'react';
import { Globe, Award, Sparkles, Shield } from 'lucide-react';

export default function AboutSection() {
  return (
    <section className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-brand-dark py-24 overflow-hidden border-y border-white/5">
      {/* Decorative Atmospheric Lighting Glow */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-accent/15 to-transparent rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-[100px] pointer-events-none" />

      <div className="relative max-w-[1170px] mx-auto px-6 z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column: Breathtaking Luxury Image Frame */}
          <div className="lg:col-span-5 flex justify-center w-full">
            <div className="relative group w-full max-w-[450px] lg:max-w-none">
              {/* Outer Golden Offset Frame */}
              <div className="absolute -inset-3 border border-accent/20 rounded-[32px] -z-10 group-hover:scale-[1.02] group-hover:border-accent/40 transition-all duration-500 pointer-events-none" />

              {/* Floating Decorative Gold Corners */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-accent/60 rounded-tl-2xl pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-accent/60 rounded-br-2xl pointer-events-none" />

              {/* Main Image Container */}
              <div className="relative overflow-hidden rounded-[24px] aspect-[4/5] shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10">
                <img
                  src="/images/luxury_travel_first_class.png"
                  alt="Faremaps Premium Travel Experience"
                  className="w-full h-full object-cover transform duration-700 ease-out group-hover:scale-[1.06]"
                />

                {/* Vignette Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent opacity-80" />

                {/* Floating Brand Badge */}
                <div className="absolute bottom-6 left-6 right-6 bg-slate-950/80 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-premium transform duration-500 group-hover:translate-y-[-4px]">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-blue-600 shadow-[0_0_12px_rgba(0,98,227,0.4)]">
                      <Sparkles className="w-5 h-5 text-white animate-pulseGlow" />
                    </div>
                    <div>
                      <span className="text-[9px] uppercase tracking-[0.2em] font-black text-sky-400 block leading-none mb-1">
                        Elite Standard
                      </span>
                      <span className="text-white font-extrabold text-sm">Elevate Your Travel</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Copywriting & Value Propositions */}
          <div className="lg:col-span-7 flex flex-col justify-center text-left">
            {/* Elegant Subtitle */}
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse shadow-[0_0_8px_#0062e3]" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-black text-transparent bg-clip-text bg-gradient-to-r from-sky-300 via-accent-light to-sky-300 font-sans">
                The Faremaps Experience
              </span>
            </div>

            {/* Main Luxury Title */}
            <h2 className="text-3xl md:text-[42px] font-extrabold text-white leading-[1.15] mb-6 font-display uppercase tracking-tight">
              Welcome to{' '}
              <span className="bg-gradient-to-r from-sky-200 via-blue-400 to-sky-200 bg-clip-text text-transparent">
                Faremaps
              </span>
            </h2>

            {/* Refined Narrative Description */}
            <div className="space-y-4 mb-10 text-slate-300 text-sm md:text-[15px] leading-relaxed font-medium">
              <p className="text-justify">
                Faremaps is a premier online airline ticket booking platform incorporated in the
                USA, offering exclusive, unpublished airfare deals with unmatched transparency and
                ease. We specialize in bespoke travel arrangements for flights originating from and
                landing across the USA, Canada, and major international hubs.
              </p>
              <p className="text-justify">
                By connecting you directly with more than 500 elite international airlines, we bring
                premium experiences in Economy, Premium Economy, Business, and luxurious First Class
                suites right to your fingertips. At Faremaps, we are constantly innovating to help
                you travel smarter, keep budgets in check, and turn every ordinary flight into an
                extraordinary journey.
              </p>
            </div>

            {/* Core Value Pillars Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Pillar 1 */}
              <div className="flex gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 text-accent-light group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white mb-1">500+ Partner Airlines</h4>
                  <p className="text-xs text-slate-400 leading-normal">
                    Access a global network of commercial and elite international carriers.
                  </p>
                </div>
              </div>

              {/* Pillar 2 */}
              <div className="flex gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 text-accent-light group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white mb-1">Premium Suite Travel</h4>
                  <p className="text-xs text-slate-400 leading-normal">
                    Unparalleled booking access for Economy, Business, and First Class cabins.
                  </p>
                </div>
              </div>

              {/* Pillar 3 */}
              <div className="flex gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 text-accent-light group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white mb-1">Bespoke Itineraries</h4>
                  <p className="text-xs text-slate-400 leading-normal">
                    Easily plan one-way, round-trip, or complex multi-city schedules.
                  </p>
                </div>
              </div>

              {/* Pillar 4 */}
              <div className="flex gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 hover:bg-white/8 transition-all duration-300 group">
                <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 text-accent-light group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white mb-1">USA-Incorporated Security</h4>
                  <p className="text-xs text-slate-400 leading-normal">
                    Book with absolute confidence under advanced, encrypted protocols.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
